import { apiFetch } from './api.js';

/**
 * Parse a filename out of a `Content-Disposition` response header.
 * Handles both the RFC 5987 `filename*=UTF-8''...` form and the plain
 * `filename="..."` form. Returns null when no usable filename is present.
 */
function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;

  // RFC 5987 extended form takes precedence when present.
  const extended = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].trim().replace(/^"|"$/g, ''));
    } catch {
      // Fall through to the plain form on a malformed encoding.
    }
  }

  const plain = /filename="?([^";]+)"?/i.exec(header);
  if (plain?.[1]) return plain[1].trim();

  return null;
}

/**
 * Download the responses CSV export for a brand, scoped by the supplied
 * filter params. Reuses `apiFetch` so a 401 transparently redirects to the
 * login flow. The download is uncapped server-side — callers must NOT pass
 * `limit`/`offset`.
 *
 * @throws Error when the server responds with a non-ok status, so callers can
 *   surface a toast / error banner.
 */
export async function downloadResponsesCsv(
  tenantId: string,
  brandId: string,
  params: URLSearchParams,
): Promise<void> {
  const qs = params.toString();
  const url =
    `/api/v1/tenants/${tenantId}/brands/${brandId}/responses/export.csv` +
    (qs ? `?${qs}` : '');

  // apiFetch handles the 401 -> /signin redirect (returns a never-resolving
  // promise in that case), so no explicit 401 branch is needed here.
  const res = await apiFetch(url, { credentials: 'same-origin' });

  if (!res.ok) {
    throw new Error(`Export failed: ${res.status} ${res.statusText}`);
  }

  const blob = await res.blob();
  const filename =
    filenameFromContentDisposition(res.headers.get('Content-Disposition')) ??
    'responses.csv';

  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
